/**
 * The seam between this chunk (the Web Audio platform layer) and the DSP core that
 * lives in `packages/client/src/recognition/core/` — a different chunk, built in
 * parallel with this one.
 *
 * The contract we were given is: `engine.ts` exposes
 *
 *     processFrame(samples: Float32Array): RecognizerEvent[]
 *     getParams(): RecognizerParams
 *     setParams(patch: Partial<RecognizerParams>): void
 *
 * What that leaves open is the *packaging* — a module-level singleton, a `createEngine`
 * factory, a class, a default export. Rather than guess and be wrong, `loadRecognizerCore`
 * probes for all four shapes and adapts whichever it finds. This is a handful of lines
 * that removes an entire class of merge-order failure, and it disappears the day the two
 * chunks are actually reconciled.
 *
 * The module is discovered through `import.meta.glob` rather than a static import
 * because it does not exist yet: a static `import '../recognition/core/engine.js'`
 * would be a compile error today, and `await import(variable)` would be un-analysable
 * by Rollup and break the production build. `import.meta.glob` is statically analysed,
 * resolves to `{}` when nothing matches, and starts working the moment the file lands
 * with no edit here.
 *
 * Until it lands, `createPlaceholderCore` keeps the audio path exercisable end to end
 * on a real iPad — see the note on its own doc comment about what it does and does not
 * do.
 */

import {
  DEFAULT_RECOGNIZER_PARAMS,
  mergeRecognizerParams,
  type RecognizerEvent,
  type RecognizerParams,
} from '@hc/shared';

export interface RecognizerCore {
  /** Called once per block from the audio path. Must not block. */
  processFrame(samples: Float32Array): RecognizerEvent[];
  getParams(): RecognizerParams;
  setParams(patch: Partial<RecognizerParams>): void;
  /** Optional. Called when the audio graph restarts, so history does not straddle it. */
  reset?(): void;
}

export interface RecognizerCoreOptions {
  /** Read from `AudioContext.sampleRate`. Never assumed — iOS picks 48000 or 44100. */
  sampleRate: number;
  params: RecognizerParams;
  /** Frames per `processFrame` call, so the core can size its own ring buffer. */
  blockSize: number;
  /**
   * `performance.now()` at the moment the graph started. The core derives its own clock
   * from the sample count (which is what makes it deterministic under test); adding this
   * origin puts `DetectedChord.timestamp` on the same timeline as the rest of the app,
   * which is what makes `timestamp - onsetTimestamp` a latency a human can compare with
   * a stopwatch.
   */
  timeOriginMs: number;
}

export type RecognizerCoreFactory = (
  options: RecognizerCoreOptions,
) => RecognizerCore | Promise<RecognizerCore>;

export interface LoadedCore {
  core: RecognizerCore;
  /** `'placeholder'` means the real DSP was not found. The UI must say so. */
  kind: 'real' | 'placeholder';
  /** Human-readable note about which shape was adapted, for the tuning screen. */
  detail: string;
}

let injectedFactory: RecognizerCoreFactory | null = null;

/**
 * Override core discovery. Tests use this; so would a future MIDI or file-playback
 * source that wanted to drive the same downstream machinery. Pass `null` to restore.
 */
export function setRecognizerCoreFactory(factory: RecognizerCoreFactory | null): void {
  injectedFactory = factory;
}

function hasCoreMethods(value: unknown): value is RecognizerCore {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<RecognizerCore>;
  return (
    typeof candidate.processFrame === 'function' &&
    typeof candidate.getParams === 'function' &&
    typeof candidate.setParams === 'function'
  );
}

/**
 * Call `fn` as a plain factory; if it turns out to be a class, `new` it instead.
 *
 * The `toString()` sniff comes first because the thrown message differs between engines
 * ("cannot be invoked without 'new'" in V8, "Cannot call a class constructor without
 * |new|" in JavaScriptCore) and getting this wrong on Safari specifically is the exact
 * failure this whole adapter exists to avoid.
 */
function invokeFactory(fn: unknown, options: RecognizerCoreOptions): unknown {
  if (typeof fn !== 'function') return undefined;
  const isClass = /^\s*class[\s{]/.test(Function.prototype.toString.call(fn));
  if (isClass) {
    return new (fn as new (o: RecognizerCoreOptions) => unknown)(options);
  }
  try {
    return (fn as (o: RecognizerCoreOptions) => unknown)(options);
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    if (!/without \|?'?new'?\|?/i.test(message)) throw error;
    return new (fn as new (o: RecognizerCoreOptions) => unknown)(options);
  }
}

const FACTORY_NAMES = [
  'createEngine',
  'createRecognizerCore',
  'createRecognizerEngine',
  'createCore',
  'default',
  // The shape the DSP chunk actually shipped: `class RecognitionEngine` taking
  // `{ sampleRate, params, timeOriginMs }`.
  'RecognitionEngine',
  'RecognizerEngine',
  'Engine',
] as const;

/** Try every packaging shape we might be handed. Returns `null` if none fit. */
export async function adaptCoreModule(
  module: unknown,
  options: RecognizerCoreOptions,
): Promise<{ core: RecognizerCore; detail: string } | null> {
  if (typeof module !== 'object' || module === null) return null;
  const bag = module as Record<string, unknown>;

  for (const name of FACTORY_NAMES) {
    const exported = bag[name];
    if (typeof exported !== 'function') {
      // `default` may itself be an already-constructed core object.
      if (name === 'default' && hasCoreMethods(exported)) {
        return { core: exported, detail: 'default export (singleton)' };
      }
      continue;
    }
    const produced = await invokeFactory(exported, options);
    if (hasCoreMethods(produced)) return { core: produced, detail: `${name}()` };
  }

  // Module-level singleton: the functions are exported directly.
  if (hasCoreMethods(bag)) {
    const singleton = bag as unknown as RecognizerCore;
    // Give it the sample rate if it offers somewhere to put it.
    const configure = bag['configure'] ?? bag['init'] ?? bag['setSampleRate'];
    if (typeof configure === 'function') {
      try {
        (configure as (o: RecognizerCoreOptions) => void).call(bag, options);
      } catch {
        /* optional hook; a mismatched signature is not fatal */
      }
    }
    singleton.setParams(options.params);
    return { core: singleton, detail: 'module singleton' };
  }

  return null;
}

/**
 * A stand-in for the real DSP.
 *
 * It does exactly one thing the platform layer can legitimately do on its own: report
 * the block's RMS level in dBFS, so that the microphone, the worklet, the message
 * plumbing and the tuning screen can all be verified against a real piano *before* the
 * DSP core exists. Chroma is reported as zeros, and **it never emits a `chord` event**.
 * If the tuning screen is showing a flat chroma and a moving energy meter, this is why.
 */
export function createPlaceholderCore(options: RecognizerCoreOptions): RecognizerCore {
  let params = mergeRecognizerParams(DEFAULT_RECOGNIZER_PARAMS, options.params);
  const zeros = () => new Array<number>(12).fill(0);
  let lastDiagnosticsAt = 0;
  let framesSinceDiagnostics = 0;
  let lastState: 'silence' | 'gathering' = 'silence';
  const secondsPerBlock = options.blockSize / Math.max(1, options.sampleRate);
  let elapsedSeconds = 0;

  return {
    processFrame(samples: Float32Array): RecognizerEvent[] {
      elapsedSeconds += secondsPerBlock;
      framesSinceDiagnostics += 1;

      let sumSquares = 0;
      for (let i = 0; i < samples.length; i += 1) sumSquares += samples[i] * samples[i];
      const rms = Math.sqrt(sumSquares / Math.max(1, samples.length));
      const energyDb = rms > 0 ? 20 * Math.log10(rms) : -160;

      const events: RecognizerEvent[] = [];
      const state = energyDb >= params.energyGateDb ? 'gathering' : 'silence';
      if (state !== lastState) {
        lastState = state;
        events.push({ type: 'state', state });
      }

      // ~10 Hz, matching the rate the diagnostics contract documents.
      if (elapsedSeconds - lastDiagnosticsAt >= 0.1) {
        const fps = framesSinceDiagnostics / Math.max(1e-6, elapsedSeconds - lastDiagnosticsAt);
        lastDiagnosticsAt = elapsedSeconds;
        framesSinceDiagnostics = 0;
        events.push({
          type: 'diagnostics',
          frame: {
            chromaInstant: zeros(),
            chromaIntegrated: zeros(),
            energyDb,
            flux: 0,
            bassPc: null,
            state,
            topHypotheses: [],
            framesPerSecond: fps,
            lastEmissionLatencyMs: null,
          },
        });
      }
      return events;
    },
    getParams: () => params,
    setParams(patch) {
      params = mergeRecognizerParams(params, patch);
    },
    reset() {
      lastDiagnosticsAt = 0;
      framesSinceDiagnostics = 0;
      elapsedSeconds = 0;
      lastState = 'silence';
    },
  };
}

/**
 * Resolve the DSP core: injected factory first, then the real module, then the
 * placeholder. Never rejects — a missing core degrades, it does not break the session.
 */
export async function loadRecognizerCore(options: RecognizerCoreOptions): Promise<LoadedCore> {
  if (injectedFactory) {
    return { core: await injectedFactory(options), kind: 'real', detail: 'injected factory' };
  }

  try {
    // Statically analysable; resolves to `{}` if `recognition/core/engine.ts` is absent.
    const modules = import.meta.glob('../recognition/core/engine.ts');
    const key = Object.keys(modules)[0];
    if (key) {
      const adapted = await adaptCoreModule(await modules[key](), options);
      if (adapted) return { core: adapted.core, kind: 'real', detail: adapted.detail };
    }
  } catch (error) {
    // A core that throws on import is a bug in that chunk, not a reason to lose audio.
    console.warn('[hc] recogniser core failed to load; using the placeholder core.', error);
  }

  return {
    core: createPlaceholderCore(options),
    kind: 'placeholder',
    detail: 'recognition/core/engine.ts not found — level metering only, no chord detection',
  };
}
